import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  UpOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { Button, Dropdown, Input, Modal, Radio, Space, Tooltip, message } from 'antd'
import type { MenuProps } from 'antd'
import {
  createAutoProcessTemplate,
  deleteAutoProcessTemplate,
  deleteTemplatePreviewSource,
  duplicateAutoProcessTemplate,
  listAutoProcessTemplates,
  runTemplateAll,
  runTemplateBackground,
  runTemplateCutout,
  runTemplateShadow,
  saveTemplateBackground,
  saveTemplateShadow,
  setActiveAutoProcessTemplate,
  setDefaultAutoProcessTemplate,
  updateAutoProcessTemplate,
  uploadTemplatePreviewSource,
  type AutoProcessTemplate,
} from '../api/client'
import { VistoeraActionPopover } from '../saas/ui/action'
import { stageStatusColor } from '../utils/status'
import { TEMPLATE_LIST_CHANGED_EVENT } from '../utils/templateEvents'
import { FourStageFlowHeader, FourStageImageFlow, bustUrl } from './StageFlow'
import BackgroundEditorModal from './editor/BackgroundEditorModal'
import ShadowEditorModal from './editor/ShadowEditorModal'
import TemplateCutoutEditorModal from './editor/TemplateCutoutEditorModal'

type Busy = 'cutout' | 'background' | 'shadow' | 'all' | null

export default function AutoProcessTemplateBar() {
  const { t } = useTranslation(['autoProcess', 'actions'])
  const [templates, setTemplates] = useState<AutoProcessTemplate[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const [draftName, setDraftName] = useState('')
  const [draftDesc, setDraftDesc] = useState('')
  const [saving, setSaving] = useState(false)

  const [busy, setBusy] = useState<Busy>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newBasis, setNewBasis] = useState<'copy' | 'default' | 'blank'>('copy')

  const [cutoutEditorOpen, setCutoutEditorOpen] = useState(false)
  const [bgEditorOpen, setBgEditorOpen] = useState(false)
  const [shadowEditorOpen, setShadowEditorOpen] = useState(false)

  // VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §7 — "删除模板" lives inside the
  // "更多" Dropdown menu, which has no per-item DOM anchor once the menu
  // closes. Pattern chosen: Dropdown menu item click closes the Dropdown
  // (AntD default) and opens this anchored `VistoeraActionPopover` on the
  // *same* "更多" trigger button (see JSX below) instead of a detached
  // centered `Modal.confirm`. Only one such Popover exists at a time since
  // this bar shows at most one expanded template.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const moreTriggerRef = useRef<HTMLSpanElement>(null)

  // Explicit outside-click + ESC handling (`trigger={[]}` on the
  // `VistoeraActionPopover` below — see JSX). Nesting a click-triggered
  // Popover directly around a Dropdown-wrapped Button leaves it
  // ambiguous whether AntD's own composed click-trigger would fire the
  // *first* "更多" click as an (unwanted) open request for THIS popover
  // rather than only opening the Dropdown; disabling the built-in
  // trigger and driving `open` purely from the menu item's `onClick`
  // removes that ambiguity, at the cost of re-implementing these two
  // standard behaviors ourselves — a single event-driven listener pair,
  // not a polling loop (spec §11/§19 perf gate).
  useEffect(() => {
    if (!deleteConfirmOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deleteBusy) setDeleteConfirmOpen(false)
    }
    const onPointerDown = (e: MouseEvent) => {
      if (deleteBusy) return
      const target = e.target as Node | null
      const insideOverlay = target instanceof Element && target.closest('.vistoera-action-popover-overlay')
      const insideTrigger = moreTriggerRef.current?.contains(target as Node)
      if (!insideOverlay && !insideTrigger) setDeleteConfirmOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [deleteConfirmOpen, deleteBusy])

  const fileInputRef = useRef<HTMLInputElement>(null)

  const reload = useCallback(async () => {
    const { data } = await listAutoProcessTemplates()
    setTemplates(data.templates)
    setActiveId(data.active_id)
    return data
  }, [])

  useEffect(() => {
    reload().catch((e) => message.error((e as Error).message || t('toast.loadTemplatesFailed')))
  }, [reload])

  // A template created elsewhere (e.g. "设为模板" in the image list) must
  // show up here immediately (spec 五/七), without a full page reload.
  useEffect(() => {
    const onChanged = () => reload().catch(() => undefined)
    window.addEventListener(TEMPLATE_LIST_CHANGED_EVENT, onChanged)
    return () => window.removeEventListener(TEMPLATE_LIST_CHANGED_EVENT, onChanged)
  }, [reload])

  const expanded = templates.find((t) => t.id === expandedId) || null
  const systemDefault = templates.find((t) => t.is_system) || null

  const openTemplate = async (template: AutoProcessTemplate) => {
    if (expandedId === template.id) {
      setExpandedId(null)
      return
    }
    try {
      await setActiveAutoProcessTemplate(template.id)
      setActiveId(template.id)
      setExpandedId(template.id)
      setDraftName(template.name)
      setDraftDesc(template.description || '')
      setTemplates((prev) => prev.map((x) => ({ ...x, is_active: x.id === template.id })))
    } catch (e) {
      message.error((e as Error).message || t('toast.switchTemplateFailed'))
    }
  }

  const saveNameDesc = async (andUse: boolean) => {
    if (!expanded) return
    if (expanded.is_system) {
      message.warning(t('toast.systemTemplateReadonly'))
      return
    }
    if (!draftName.trim()) {
      message.warning(t('toast.templateNameRequired'))
      return
    }
    if (templates.some((tpl) => tpl.id !== expanded.id && tpl.name === draftName.trim())) {
      message.warning(t('toast.templateNameDuplicate'))
      return
    }
    setSaving(true)
    try {
      await updateAutoProcessTemplate(expanded.id, { name: draftName, description: draftDesc })
      if (andUse) await setActiveAutoProcessTemplate(expanded.id)
      message.success(andUse ? t('toast.templateSavedAndActive') : t('toast.templateSaved'))
      await reload()
    } catch (e) {
      message.error((e as Error).message || t('toast.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) {
      message.warning(t('toast.createNameRequired'))
      return
    }
    if (templates.some((tpl) => tpl.name === name)) {
      message.warning(t('toast.templateNameDuplicate'))
      return
    }
    try {
      const copyFromId = newBasis === 'copy' ? expandedId || activeId || undefined : newBasis === 'default' ? systemDefault?.id : undefined
      const { data } = await createAutoProcessTemplate({ name, copy_from_id: copyFromId, set_active: false })
      setCreateOpen(false)
      setNewName('')
      await reload()
      setExpandedId(data.id)
      setDraftName(data.name)
      setDraftDesc(data.description || '')
      message.success(t('toast.templateCreated'))
    } catch (e) {
      message.error((e as Error).message || t('toast.createFailed'))
    }
  }

  const requireCopyIfSystem = (template: AutoProcessTemplate, andThen: (copy: AutoProcessTemplate) => void) => {
    if (!template.is_system) {
      andThen(template)
      return
    }
    Modal.confirm({
      // See centering note in `useChainDeleteActions.tsx` — static
      // Modal.confirm doesn't inherit ConfigProvider's `modal.centered`.
      centered: true,
      title: t('toast.systemTemplateConfirmTitle'),
      content: t('toast.systemTemplateConfirmContent'),
      okText: t('toast.copyAndEdit'),
      cancelText: t('actions:cancel'),
      onOk: async () => {
        const { data } = await duplicateAutoProcessTemplate(template.id)
        await reload()
        setExpandedId(data.id)
        setDraftName(data.name)
        setDraftDesc(data.description || '')
        andThen(data)
      },
    })
  }

  const moreMenu = (tpl: AutoProcessTemplate): MenuProps => ({
    items: [
      {
        key: 'default',
        label: t('templateBar.setDefault'),
        disabled: tpl.is_default,
        onClick: async () => {
          await setDefaultAutoProcessTemplate(tpl.id)
          await reload()
          message.success(t('toast.setDefaultSuccess'))
        },
      },
      {
        key: 'dup',
        icon: <CopyOutlined />,
        label: t('templateBar.duplicate'),
        onClick: async () => {
          const { data } = await duplicateAutoProcessTemplate(tpl.id)
          await reload()
          setExpandedId(data.id)
          setDraftName(data.name)
          setDraftDesc(data.description || '')
          message.success(t('toast.duplicated'))
        },
      },
      {
        key: 'del',
        icon: <DeleteOutlined />,
        danger: true,
        label: t('templateBar.delete'),
        disabled: tpl.is_system,
        onClick: () => {
          // Dropdown closes itself (AntD default menu-item-click behavior);
          // the anchored confirm Popover opens next, on the same "更多"
          // trigger button — see `deleteConfirmOpen` JSX below.
          setDeleteConfirmOpen(true)
        },
      },
    ],
  })

  const doDeleteTemplate = async (tpl: AutoProcessTemplate) => {
    setDeleteBusy(true)
    try {
      await deleteAutoProcessTemplate(tpl.id)
      if (expandedId === tpl.id) setExpandedId(null)
      await reload()
      message.success(t('toast.deleted'))
      setDeleteConfirmOpen(false)
    } catch (e) {
      message.error((e as Error).message || t('autoProcess:deleteTemplate.deleteFailed'))
    } finally {
      setDeleteBusy(false)
    }
  }

  // --- Test image upload/replace/delete -----------------------------------
  const pendingUploadTemplateId = useRef<string | null>(null)
  const triggerUpload = (templateId: string) => {
    pendingUploadTemplateId.current = templateId
    fileInputRef.current?.click()
  }
  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const templateId = pendingUploadTemplateId.current
    e.target.value = ''
    if (!file || !templateId) return
    try {
      await uploadTemplatePreviewSource(templateId, file)
      message.success(t('toast.testImageUploaded'))
      setBusy('all')
      await runTemplateAll(templateId)
      await reload()
    } catch (err) {
      message.error((err as Error).message || t('toast.uploadOrProcessFailed'))
    } finally {
      setBusy(null)
    }
  }
  const deleteTestImage = async (templateId: string) => {
    await deleteTemplatePreviewSource(templateId)
    await reload()
  }

  // --- Stage run/retry ------------------------------------------------------
  const retryCutout = async (template: AutoProcessTemplate) => {
    setBusy('cutout')
    try {
      await runTemplateCutout(template.id)
      await reload()
    } catch (e) {
      message.error((e as Error).message || t('toast.cutoutRetryFailed'))
    } finally {
      setBusy(null)
    }
  }
  const retryBackground = async (template: AutoProcessTemplate) => {
    setBusy('background')
    try {
      await runTemplateBackground(template.id)
      await reload()
    } catch (e) {
      message.error((e as Error).message || t('toast.layoutRetryFailed'))
    } finally {
      setBusy(null)
    }
  }
  const retryShadow = async (template: AutoProcessTemplate) => {
    setBusy('shadow')
    try {
      await runTemplateShadow(template.id)
      await reload()
    } catch (e) {
      message.error((e as Error).message || t('toast.shadowRetryFailed'))
    } finally {
      setBusy(null)
    }
  }
  const runAll = async (template: AutoProcessTemplate) => {
    if (!template.preview_source_url) {
      message.warning(t('toast.uploadTestImageFirst'))
      return
    }
    setBusy('all')
    try {
      await runTemplateAll(template.id)
      await reload()
      message.success(t('toast.rerunAllSuccess'))
    } catch (e) {
      message.error((e as Error).message || t('toast.runFailed'))
    } finally {
      setBusy(null)
    }
  }

  const activeName = templates.find((t) => t.id === activeId)?.name || '—'

  return (
    <div className="apt-bar" data-testid="auto-process-template-bar">
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFileSelected} />

      <div className="apt-bar-row">
        <span className="apt-bar-label">{t('templateBar.label')}</span>
        <div className="apt-chip-list">
          {templates.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              className={[
                'apt-chip',
                tpl.id === activeId ? 'apt-chip-active' : '',
                tpl.id === expandedId ? 'apt-chip-editing' : '',
                tpl.is_default ? 'apt-chip-default' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => openTemplate(tpl)}
              title={tpl.description || tpl.name}
            >
              {tpl.id === activeId && <CheckOutlined className="apt-chip-check" />}
              <span>{tpl.name}</span>
              {tpl.is_default && <span className="apt-chip-tag">{t('templateBar.defaultTag')}</span>}
            </button>
          ))}
          <button
            type="button"
            className="apt-chip apt-chip-new"
            onClick={() => {
              setNewBasis('copy')
              setCreateOpen(true)
            }}
          >
            <PlusOutlined /> {t('templateBar.newTemplate')}
          </button>
        </div>
        <span className="apt-bar-current">
          {t('templateBar.currentTemplate')}<strong>{activeName}</strong>
        </span>
      </div>

      {expanded && (
        <div className="apt-drawer" data-testid="apt-drawer">
          <div className="apt-drawer-head">
            <div className="apt-drawer-title">
              <Input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                variant="borderless"
                className="apt-name-input"
                disabled={expanded.is_system}
              />
              {expanded.is_system && <span className="apt-chip-tag">{t('templateBar.systemReadonlyTag')}</span>}
            </div>
            <Space>
              <VistoeraActionPopover
                open={deleteConfirmOpen}
                trigger={[]}
                placement="bottomRight"
                onOpenChange={(next) => {
                  if (!next && !deleteBusy) setDeleteConfirmOpen(false)
                }}
                content={
                  <div className="vistoera-action-confirm">
                    <div className="vistoera-action-confirm-title">
                      {t('autoProcess:deleteTemplate.confirmTitleWithName', { name: expanded.name })}
                    </div>
                    <div className="vistoera-action-confirm-desc">
                      {expanded.is_active
                        ? t('autoProcess:deleteTemplate.confirmDescriptionActive')
                        : t('autoProcess:deleteTemplate.confirmDescriptionInactive')}
                    </div>
                    <div className="vistoera-action-confirm-actions">
                      <Button size="small" disabled={deleteBusy} onClick={() => setDeleteConfirmOpen(false)}>
                        {t('actions:cancel')}
                      </Button>
                      <Button size="small" danger type="primary" loading={deleteBusy} onClick={() => doDeleteTemplate(expanded)}>
                        {t('autoProcess:deleteTemplate.confirmLabel')}
                      </Button>
                    </div>
                  </div>
                }
              >
                <span ref={moreTriggerRef} style={{ display: 'inline-flex' }}>
                  <Dropdown menu={moreMenu(expanded)} trigger={['click']}>
                    <Button size="small">{t('templateBar.more')}</Button>
                  </Dropdown>
                </span>
              </VistoeraActionPopover>
              <Button size="small" icon={<UpOutlined />} onClick={() => setExpandedId(null)}>
                {t('templateBar.collapse')}
              </Button>
              {!expanded.is_system && (
                <>
                  <Button size="small" loading={saving} onClick={() => saveNameDesc(false)}>
                    {t('templateBar.saveTemplate')}
                  </Button>
                  <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => saveNameDesc(true)}>
                    {t('templateBar.saveAndUse')}
                  </Button>
                </>
              )}
            </Space>
          </div>
          <Input.TextArea
            rows={1}
            placeholder={t('templateBar.descriptionPlaceholder')}
            value={draftDesc}
            onChange={(e) => setDraftDesc(e.target.value)}
            className="apt-desc"
            disabled={expanded.is_system}
          />

          <div className="apt-testimg-row">
            <Space wrap>
              <Button size="small" icon={<UploadOutlined />} onClick={() => triggerUpload(expanded.id)}>
                {expanded.preview_source_url ? t('templateBar.replaceTestImage') : t('templateBar.uploadTestImage')}
              </Button>
              {expanded.preview_source_url && (
                <Button size="small" danger onClick={() => deleteTestImage(expanded.id)}>
                  {t('templateBar.deleteTestImage')}
                </Button>
              )}
              <Tooltip title={t('templateBar.rerunAllTooltip')}>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={busy === 'all'}
                  disabled={!expanded.preview_source_url}
                  onClick={() => runAll(expanded)}
                >
                  {t('templateBar.rerunAll')}
                </Button>
              </Tooltip>
            </Space>
          </div>

          {/* Always render the full four-stage layout — a brand-new template
           * with no test image yet is NOT a blank page (spec 一): only the
           * 原图 cell is empty (click-to-upload); 透明底图/色底图/阴影图 show
           * a fixed "等待生成" placeholder in their normal slot. Uploading a
           * test image only fills content in, it never changes this layout. */}
          <div className="apt-flow-row">
            <FourStageFlowHeader />
            <FourStageImageFlow
              original={{
                checker: false,
                url: bustUrl(expanded.preview_source_url, expanded.template_version),
                // Always reserve the subtitle/meta lines (even blank) so
                // uploading a test image only fills content in and never
                // shifts the row's height (spec 一: 上传图片以后 UI 布局
                // 完全不变).
                subtitle: expanded.preview_source_url ? t('templateBar.testImageSubtitle') : '\u00A0',
                meta: expanded.preview_source_url ? t('templateBar.testImageMeta') : '\u00A0',
                emptySlot: !expanded.preview_source_url ? (
                  <button
                    type="button"
                    className="four-stage-upload-empty"
                    onClick={() => triggerUpload(expanded.id)}
                  >
                    {t('templateBar.clickToUpload')}
                  </button>
                ) : undefined,
              }}
              transparent={{
                checker: true,
                url: bustUrl(expanded.preview_transparent_url, expanded.template_version),
                statusLabel: expanded.cutout_status_label,
                statusColor: stageStatusColor[expanded.cutout_status],
                error: expanded.cutout_error,
                busy: busy === 'cutout' || busy === 'all',
                showRetry: expanded.cutout_status === 'failed' || expanded.cutout_status === 'stale',
                onRetry: () => retryCutout(expanded),
                emptySlot: expanded.cutout_status === 'idle' ? <span className="four-stage-waiting">{t('stages.waitingGeneration')}</span> : undefined,
              }}
              background={{
                url: bustUrl(expanded.preview_background_url, expanded.template_version),
                statusLabel: expanded.background_status_label,
                statusColor: stageStatusColor[expanded.background_status],
                error: expanded.background_error,
                busy: busy === 'background' || busy === 'all',
                showRetry: expanded.background_status === 'failed' || expanded.background_status === 'stale',
                onRetry: () => retryBackground(expanded),
                emptySlot: expanded.background_status === 'idle' ? <span className="four-stage-waiting">{t('stages.waitingGeneration')}</span> : undefined,
              }}
              shadow={{
                url: bustUrl(expanded.preview_shadow_url, expanded.template_version),
                statusLabel: expanded.shadow_status_label,
                statusColor: stageStatusColor[expanded.shadow_status],
                error: expanded.shadow_error,
                busy: busy === 'shadow' || busy === 'all',
                showRetry: expanded.shadow_status === 'failed' || expanded.shadow_status === 'stale',
                onRetry: () => retryShadow(expanded),
                emptySlot: expanded.shadow_status === 'idle' ? <span className="four-stage-waiting">{t('stages.waitingGeneration')}</span> : undefined,
              }}
              toTransparent={{
                label: t('templateBar.editCutout'),
                disabled: !expanded.preview_source_url,
                onClick: () => requireCopyIfSystem(expanded, () => setCutoutEditorOpen(true)),
              }}
              toBackground={{
                label: t('templateBar.editLayout'),
                disabled: expanded.cutout_status !== 'done',
                onClick: () => requireCopyIfSystem(expanded, () => setBgEditorOpen(true)),
              }}
              toShadow={{
                label: t('templateBar.editShadow'),
                disabled: expanded.background_status !== 'done',
                onClick: () => requireCopyIfSystem(expanded, () => setShadowEditorOpen(true)),
              }}
            />
          </div>

          <div className="apt-flow-status-row">
            <span className="apt-flow-status-item">{t('templateBar.cutoutStatus', { label: expanded.cutout_status_label })}</span>
            <span className="apt-flow-status-item">{t('templateBar.layoutStatus', { label: expanded.background_status_label })}</span>
            <span className="apt-flow-status-item">{t('templateBar.shadowStatus', { label: expanded.shadow_status_label })}</span>
          </div>
        </div>
      )}

      <Modal title={t('templateBar.createTitle')} open={createOpen} onCancel={() => setCreateOpen(false)} footer={null} destroyOnClose>
        <div className="apt-create-form">
          <label className="apt-field">
            <span>{t('templateBar.templateName')}</span>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('templateBar.templateNamePlaceholder')} autoFocus />
          </label>
          <label className="apt-field">
            <span>{t('templateBar.initialParams')}</span>
            <Radio.Group value={newBasis} onChange={(e) => setNewBasis(e.target.value)}>
              <Space direction="vertical">
                <Radio value="copy" disabled={!expandedId && !activeId}>
                  {t('templateBar.basisCopy')}
                </Radio>
                <Radio value="default">{t('templateBar.basisDefault')}</Radio>
                <Radio value="blank">{t('templateBar.basisBlank')}</Radio>
              </Space>
            </Radio.Group>
          </label>
          <Space style={{ marginTop: 8 }}>
            <Button onClick={() => setCreateOpen(false)}>{t('actions:cancel')}</Button>
            <Button type="primary" onClick={handleCreate}>
              {t('actions:create')}
            </Button>
          </Space>
        </div>
      </Modal>

      {expanded && cutoutEditorOpen && (
        <TemplateCutoutEditorModal
          template={expanded}
          open={cutoutEditorOpen}
          onClose={() => setCutoutEditorOpen(false)}
          onSaved={() => {
            reload()
          }}
        />
      )}

      {expanded && bgEditorOpen && expanded.preview_transparent_url && (
        <BackgroundEditorModal
          chainId={expanded.id}
          filename={expanded.name}
          transparentUrl={bustUrl(expanded.preview_transparent_url, expanded.template_version) || expanded.preview_transparent_url}
          initialParams={expanded.params.background}
          open={bgEditorOpen}
          onClose={() => setBgEditorOpen(false)}
          titlePrefix={t('templateBar.bgEditorTitlePrefix')}
          onSaveOverride={async (blob, params) => {
            await saveTemplateBackground(expanded.id, blob, params)
            await reload()
          }}
        />
      )}

      {expanded && shadowEditorOpen && expanded.preview_transparent_url && (
        <ShadowEditorModal
          chainId={expanded.id}
          filename={expanded.name}
          transparentUrl={bustUrl(expanded.preview_transparent_url, expanded.template_version) || expanded.preview_transparent_url}
          backgroundParams={expanded.params.background}
          initialShadowParams={expanded.params.shadow}
          open={shadowEditorOpen}
          onClose={() => setShadowEditorOpen(false)}
          titlePrefix={t('templateBar.shadowEditorTitlePrefix')}
          onSaveOverride={async (blob, params) => {
            await saveTemplateShadow(expanded.id, blob, params)
            await reload()
          }}
        />
      )}
    </div>
  )
}
